// src/auth/authConfig.js

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */

// These will be fetched from the backend
let msalConfig = null;

// Initialize configuration from backend
export const initializeMsalConfig = async () => {
  try {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || 
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
        ? 'http://localhost:8082' 
        : `${window.location.origin}/api`);
    const response = await fetch(`${baseUrl}/auth/config`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch auth config: ${response.status}`);
    }
    
    const config = await response.json();
    
    msalConfig = {
      auth: {
        clientId: config.clientId, // This is the ONLY mandatory field
        authority: config.authority, // Azure AD Tenant ID authority
        redirectUri: config.redirectUri, // Must be registered as a redirect URI in Azure AD
        postLogoutRedirectUri: window.location.origin, // After logout, redirect here
      },
      cache: {
        cacheLocation: "localStorage", // This configures where your cache will be stored
        storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
      },
      system: {
        loggerOptions: {
          loggerCallback: () => {
            // Logging disabled in production
          }
        }
      }
    };
    
    return msalConfig;
  } catch (error) {
    // Fallback configuration (should not be used in production)
    msalConfig = {
      auth: {
        clientId: "31bdbc65-1851-4395-bb14-47842c2a3173", // Replace with your actual client ID
        authority: "https://login.microsoftonline.com/43ef39ce-7d3c-4656-9760-3d8bb5af2bfa",
        redirectUri: window.location.origin + "/auth/callback",
        postLogoutRedirectUri: window.location.origin,
      },
      cache: {
        cacheLocation: "localStorage",
        storeAuthStateInCookie: false,
      },
      system: {
        loggerOptions: {
          loggerCallback: () => {
            // Logging disabled in production
          }
        }
      }
    };
    
    return msalConfig;
  }
};

export const getMsalConfig = () => msalConfig;

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
  scopes: ["User.Read"]
};

/**
 * Add here the scopes to request when obtaining an access token for MS Graph API. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const graphConfig = {
  graphMeEndpoint: "https://graph.microsoft.com/v1.0/me"
};

/**
 * Scope for the SharePoint access check only: it lets KAI ask Graph, as the
 * signed-in user, whether THEY can open a file they pasted a link to. It is
 * deliberately NOT in loginRequest above -- sign-in stays profile-only, and
 * this is acquired on demand, on the turns where a link is actually pasted.
 *
 * Files.Read.All is user-consentable, so no admin consent is required: each
 * person consents once for themselves, on the dashboard verification page,
 * and MSAL serves it from cache after that. Until they have, silent
 * acquisition fails and the check is skipped rather than refused -- see
 * getGraphFileToken in AuthContext.js.
 */
export const graphFileRequest = {
  scopes: ["Files.Read.All"]
};
